import * as z from 'zod'

import { timestampSchema } from '#schemas'
import { submissionSchema } from '#submissions/schemas'
import type { Topic } from '#survey/schemas/topic'

const nonEmptyIdSchema = z.string().trim().min(1)

// One value override on one field of one record.
//
// `value` is ABSOLUTE, in the record's own units — annual dollars for
// `annualValue`, regardless of how the underlying answer was entered. That is the
// point of overriding the parsed record: the moneyWithPeriod annualisation has
// already happened, so a quest never has to know whether the plan captured a
// monthly or a yearly figure.
//
// `baseValue` is the parsed value the override was made against, and it exists
// because nothing else can detect drift. Submissions carry no updatedAt, so if the
// plan's rent moves 2000 -> 2200 under a "-500" override, the override silently
// becomes a -700. Holding the base makes that exactly detectable
// (`baseValue !== record.annualValue`), lets the sheet offer "keep $1,500" or "cut
// $500 again", and keeps a later switch to relative mode recoverable since the
// original ratio is `value / baseValue`. Optional, so it is additive.
//
// TODO(ZL-815): decide whether a relative mode is ever needed. Absolute pins a
// figure ("I found a place at $1,500"); relative preserves an intent ("cut rent by
// a quarter") when the plan value later changes. Absolutes only for now.
export const recordOverrideEntrySchema = z.strictObject({
  value: z.number(),
  baseValue: z.number().optional(),
  updatedAt: timestampSchema,
})

// A window in years of age, matching the `AgeRange` the bounds resolver works in.
//
// Ages rather than dates so the figure survives the projection rolling into a new
// year: "until 62" stays true in January, a stored 2031-04-01 does not.
//
// Both ends optional, because a tuning may declare `direction: start` or
// `direction: end` and move exactly one pole. An absent end is not "no end" — it
// means this quest left that pole where the plan put it.
const storedAgeRangeSchema = z.strictObject({
  startAge: z.number().optional(),
  endAge: z.number().optional(),
})

// The same value/baseValue/updatedAt triple as a money override, over a pair of
// ages instead of one number. `baseValue` carries the plan's own window at the
// moment of the edit, for the same drift reason spelled out above.
export const dateRangeOverrideEntrySchema = z.strictObject({
  value: storedAgeRangeSchema,
  baseValue: storedAgeRangeSchema.optional(),
  updatedAt: timestampSchema,
})

// What one quest stores per field of one record.
//
// An explicit key per field, NOT a record keyed by `recordFieldSchema`, because
// the shapes genuinely differ: a money override is a number, a date-range
// override is a pair of ages. One map of one shape could only hold both by making
// the value a union, which pushes a runtime check into every reader — the key
// already says which shape it is.
//
// The cost is that this object and the enum can drift, so a guard test in
// schemas.test.ts asserts the keys here cover every declarable field.
export const recordFieldOverridesSchema = z.strictObject({
  annualValue: recordOverrideEntrySchema.optional(),
  dateRange: dateRangeOverrideEntrySchema.optional(),
})

// Whether the quest counts this record in its scenario.
//
// Stored explicitly as true/false with a timestamp rather than by presence, so
// turning a record back on is a first-class act that can win the record's
// most-recent-edit contest. Presence-only muting left unmute with nothing to
// store, so one quest could never re-enable a record another quest had switched
// off.
export const recordMuteSchema = z.strictObject({
  value: z.boolean(),
  updatedAt: timestampSchema,
})

// Everything one quest does to one plan record.
//
// Two sections because the two kinds of change have different scopes: a value
// override is per field, while muting is per record — there is no such thing as
// muting a field. Encoding the mute as a reserved pseudo-field would put a
// boolean in a numeric slot and make "is this record off" something a reader has
// to infer from a magic value.
//
// `fields` holds one entry per adjusted field — see `recordFieldOverridesSchema`
// for why each field's shape is spelled out rather than keyed by the enum.
export const recordOverridesEntrySchema = z.strictObject({
  muted: recordMuteSchema.optional(),
  fields: recordFieldOverridesSchema.optional(),
})

// Everything the quest does to the PARSED records of other submissions — the
// single home for quest-to-plan changes, replacing both `tunings` (answer
// substitution) and `mutedSubmissions` (a parallel map that had to be
// cross-referenced to reconstruct one record's timeline).
//
// Keyed by target submissionId, which addresses that submission's ROOT record:
// its id is `generateId(submissionId, null, 0)`, a pure function of the submission
// id with no positional component, so the key survives any reordering of a topic's
// `finRecordBuilder.children`.
//
// TODO(ZL-815): child records (a mortgage under a home purchase) are NOT
// addressable with this key, and must not be bolted on positionally. `childIndex`
// is the index in the builder's `children:` array, so a record id derived from it
// silently re-targets when anyone inserts or reorders a builder. Targeting a child
// needs a declared, semantic key on the builder. Root-only until then; extending
// adds a new optional segment and leaves everything stored here untouched.
export const questRecordOverridesSchema = z.record(
  nonEmptyIdSchema,
  recordOverridesEntrySchema
)

export const questSchema = submissionSchema.extend({
  templateId: z.string(),
  // Which journey this quest belongs to. Optional, and absent means the base
  // journey: every quest written before journeys existed belongs to the one
  // every user already had, so nothing has to be migrated. See `questJourney`.
  journeyId: z.string().optional(),
  // TODO(ZL-815): revisit persistence for `enabled`. It might stay in MMKV,
  // move to Firestore once quest sync lands, or become purely session-local.
  enabled: z.boolean().default(true),
  // User-supplied display name that overrides the record's template-derived
  // name. Optional so quests default to the auto-generated title from the
  // topic's `name:` expression until the user explicitly renames.
  name: z.string().trim().min(1).max(60).optional(),
  // Everything this quest does to other submissions' parsed records: value
  // overrides and muting. Replaced `tunings` (answer substitution before the
  // build) and `mutedSubmissions` (a parallel map for the same records).
  recordOverrides: questRecordOverridesSchema.optional(),
})

export type RecordMute = z.output<typeof recordMuteSchema>
export type RecordOverrideEntry = z.output<typeof recordOverrideEntrySchema>
export type DateRangeOverrideEntry = z.output<
  typeof dateRangeOverrideEntrySchema
>
export type RecordFieldOverrides = z.output<typeof recordFieldOverridesSchema>
export type RecordOverridesEntry = z.output<typeof recordOverridesEntrySchema>
export type QuestRecordOverrides = z.output<typeof questRecordOverridesSchema>
export type Quest = z.output<typeof questSchema>

export interface QuestDoc {
  id: string
  entity: Quest
}

// A quest template is structurally a survey Topic — quest templates are
// bundled as the topics of a synthetic 'quests' Survey so they flow through
// SurveyCollection and buildFinancialRecords unchanged.
export type QuestTemplate = Topic
